'use client';

import { datadogRum } from '@datadog/browser-rum';
import { TableBody, TableHeader } from '@ria-client/tw-shadcn/components/table';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { startTransition, useEffect, useRef, useState } from 'react';
import { ProductListData } from './product-list-data';
import { SkeletonRow } from './skeleton-row';
import { SummaryTableContainer } from './summary-table-container';
import { SummaryTableHead } from './summary-table-head';
import { SummaryTableRow } from './summary-table-row';
import type { SummaryTableRowWithDrivers } from './types';

export interface SummaryTableProps {
  headings: { label?: string; labelTooltip?: string }[];
  rows: SummaryTableRowWithDrivers[];
  maxRows?: number;
  numTruncatedRows?: number;
  allowTruncatingRows?: boolean;
  showSkeleton?: boolean;
  slug?: string;
  pageUrl: string;
  isFilterActive?: boolean;
  isAccordionActiveButtonActive?: boolean;
}

function isMultiProductBannerEnabledForSlug(slug: string) {
  return ['money-market-accounts'].includes(slug);
}

export function SummaryTable({
  headings,
  rows,
  maxRows,
  numTruncatedRows,
  allowTruncatingRows,
  showSkeleton = false,
  slug,
  pageUrl,
  isFilterActive,
  isAccordionActiveButtonActive,
}: SummaryTableProps) {
  const isMultiProductBannerEnabled = isMultiProductBannerEnabledForSlug(slug ?? '');
  const rowsToShow = isMultiProductBannerEnabled ? 4 : numTruncatedRows;
  const [rowsAreTruncated, setRowsAreTruncated] = useState(true);
  const truncationEnabled = Boolean(
    (isMultiProductBannerEnabled ||
      isAccordionActiveButtonActive ||
      (allowTruncatingRows && maxRows && rows.length > maxRows)) &&
    !isFilterActive,
  );
  const visibleRows = truncationEnabled && rowsAreTruncated ? rows.slice(0, rowsToShow) : rows;
  const firedSummaryTableShown = useRef(false);

  useEffect(() => {
    if (!showSkeleton && !firedSummaryTableShown.current) {
      datadogRum.addTiming('summary_table_shown');
      firedSummaryTableShown.current = true;
    }
  }, [showSkeleton]);

  const toggleRows = () => {
    if (!rowsAreTruncated) {
      document.getElementById('summary-table-headers')?.scrollIntoView({ behavior: 'smooth' });
    }
    startTransition(() => setRowsAreTruncated(value => !value));
  };
  const isBannerButton = isMultiProductBannerEnabled || isAccordionActiveButtonActive;
  const label = rowsAreTruncated
    ? isBannerButton
      ? 'See more accounts'
      : 'View more'
    : isBannerButton
      ? 'See less accounts'
      : 'View fewer';
  const Icon = rowsAreTruncated ? ChevronDown : ChevronUp;

  return (
    <>
      <SummaryTableContainer>
        <TableHeader id="summary-table-headers">
          <SummaryTableHead headings={headings} />
        </TableHeader>
        <TableBody>
          {visibleRows.map((row, index) =>
            showSkeleton ? (
              <SkeletonRow
                driversLength={row.marketplaceEntity.drivers.length}
                key={`skeleton-${index}`}
              />
            ) : (
              <SummaryTableRow index={index} key={index} row={row} />
            ),
          )}
        </TableBody>
      </SummaryTableContainer>
      {truncationEnabled ? (
        isBannerButton ? (
          <div className="flex justify-center pt-5">
            <button
              className="flex h-12 w-full items-center justify-center gap-2 rounded-sm border border-primary bg-white px-3 text-primary md:w-fit"
              onClick={toggleRows}
              type="button"
            >
              <span className="typography-text-small font-medium uppercase">{label}</span>
              <Icon aria-hidden size={16} />
            </button>
          </div>
        ) : (
          <button
            className="typography-text-small mt-1 font-medium text-primary"
            onClick={toggleRows}
            type="button"
          >
            {label}
          </button>
        )
      ) : null}
      <ProductListData
        id="product-list-linked-data"
        offers={visibleRows}
        title="product-list-linked-data"
        url={pageUrl}
      />
    </>
  );
}
